import type { Booking } from '../api/schemas';
import type { ClassSessionRecord } from '../api/endpoints/sessions';
import { isSameDay } from './format';

/** Statuses that hold a place in the room (or explain why it stayed empty). */
const SEATED = new Set<Booking['status']>(['confirmed', 'completed', 'paid', 'no_show']);
const ARRIVED = new Set<Booking['status']>(['completed', 'paid']);
/** Money only counts once it was charged: a place that is paid. */
const BILLED = new Set<Booking['status']>(['paid']);
const WEEK_DAYS = 7;
const DAY_MS = 86_400_000;

export type CoachClass = {
  id: string;
  serviceId: string;
  startsAt: string;
  endsAt: string;
  capacity: number;
  isCancelled: boolean;
  isPast: boolean;
  /** Started and not yet over. */
  isLive: boolean;
  attendees: Booking[];
  arrived: number;
  /** What this class billed: the paid places, in cents. */
  billedCents: number;
};

export type CoachDay = { key: string; date: Date; classes: CoachClass[] };

export type CoachWeek = {
  /** Today onward, soonest first. */
  upcoming: CoachDay[];
  /** Before today, most recent first. */
  past: CoachDay[];
  /** The class running now, else the closest one ahead; cancelled ones never. */
  current: CoachClass | undefined;
  stats: { today: number; classes: number; people: number; fillPercent: number | null };
  /** What this coach's work billed this calendar month; a class counts once, an appointment once each. */
  month: { billedCents: number; paidBookings: number; visits: number };
};

/**
 * A coach's classes, grouped by day, with who holds a place in each. The API
 * already narrowed both lists to this coach; this only arranges them.
 */
export function buildCoachWeek(sessions: ClassSessionRecord[], bookings: Booking[], now: Date): CoachWeek {
  const todayStart = startOfDay(now);
  const classes = [...sessions]
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt))
    .map((session) => toCoachClass(session, bookings, now));

  const days = groupByDay(classes);
  const upcoming = days.filter((day) => day.date.getTime() >= todayStart.getTime());
  const past = days.filter((day) => day.date.getTime() < todayStart.getTime()).reverse();
  const live = classes.filter((entry) => !entry.isCancelled && !entry.isPast);
  const current = live.find((entry) => entry.isLive) ?? live[0];
  return { upcoming, past, current, stats: weekStats(classes, now), month: monthBilled(bookings, now) };
}

/** "Hoy", "Mañana", "Ayer", else the weekday and date spelled out, sentence case. */
export function dayHeading(date: Date, now: Date): string {
  if (isSameDay(date, now)) return 'Hoy';
  if (isSameDay(date, addDays(now, 1))) return 'Mañana';
  if (isSameDay(date, addDays(now, -1))) return 'Ayer';
  const text = date.toLocaleDateString('es-PA', { weekday: 'long', day: 'numeric', month: 'long' });
  return text.charAt(0).toUpperCase() + text.slice(1).replace(',', '');
}

function toCoachClass(session: ClassSessionRecord, bookings: Booking[], now: Date): CoachClass {
  const attendees = bookings
    .filter((booking) => booking.sessionId === session.id && SEATED.has(booking.status))
    .sort((a, b) => (a.customerName ?? '').localeCompare(b.customerName ?? '', 'es'));
  return {
    id: session.id,
    serviceId: session.serviceId,
    startsAt: session.startsAt,
    endsAt: session.endsAt,
    capacity: session.capacity,
    isCancelled: session.status === 'cancelled',
    isPast: Date.parse(session.endsAt) <= now.getTime(),
    isLive: Date.parse(session.startsAt) <= now.getTime() && now.getTime() < Date.parse(session.endsAt),
    attendees,
    arrived: attendees.filter((booking) => ARRIVED.has(booking.status)).length,
    billedCents: sumBilled(attendees),
  };
}

function sumBilled(bookings: Booking[]): number {
  return bookings.filter((booking) => BILLED.has(booking.status)).reduce((sum, booking) => sum + booking.amountCents, 0);
}

function monthBilled(bookings: Booking[], now: Date): CoachWeek['month'] {
  const paid = bookings.filter((booking) => {
    const starts = new Date(booking.startsAt);
    return BILLED.has(booking.status) && starts.getFullYear() === now.getFullYear() && starts.getMonth() === now.getMonth();
  });
  const visits = new Set(paid.map((booking) => booking.sessionId ?? booking.id));
  return { billedCents: sumBilled(paid), paidBookings: paid.length, visits: visits.size };
}

function groupByDay(classes: CoachClass[]): CoachDay[] {
  return classes.reduce<CoachDay[]>((days, entry) => {
    const date = startOfDay(new Date(entry.startsAt));
    const key = date.toISOString();
    const last = days[days.length - 1];
    if (last?.key === key) return [...days.slice(0, -1), { ...last, classes: [...last.classes, entry] }];
    return [...days, { key, date, classes: [entry] }];
  }, []);
}

/** Today plus the six days after it; cancelled classes do not count. */
function weekStats(classes: CoachClass[], now: Date): CoachWeek['stats'] {
  const from = startOfDay(now).getTime();
  const to = from + WEEK_DAYS * DAY_MS;
  const ahead = classes.filter((entry) => {
    const starts = Date.parse(entry.startsAt);
    return !entry.isCancelled && starts >= from && starts < to;
  });
  const people = ahead.reduce((sum, entry) => sum + entry.attendees.length, 0);
  const seats = ahead.reduce((sum, entry) => sum + entry.capacity, 0);
  return {
    today: ahead.filter((entry) => isSameDay(new Date(entry.startsAt), now)).length,
    classes: ahead.length,
    people,
    fillPercent: seats > 0 ? Math.round((people / seats) * 100) : null,
  };
}

function startOfDay(date: Date): Date {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  return start;
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}
